import * as Toast from "@radix-ui/react-toast";

export const NotificationsContext = createContext({
  notifications: [] as NotificationToast[],
  setNotifications: (notifications: NotificationToast[]) => {},
});

import type { NotificationToast } from "@common/services/common-events";
import { CommonEvents } from "@common/services/common-events";
import classNames from "classnames";
import type { ComponentProps, FC} from "react";
import { createContext, useCallback, useEffect, useState } from "react";
import { PiXBold } from "react-icons/pi";
import { twMerge } from "tailwind-merge";
import { Button } from "../app/components/button";

interface NotificationToastsProps {}

// eslint-disable-next-line mobx/missing-observer
export const NotificationToasts: FC<NotificationToastsProps> = ({}) => {
  const [notifications, setNotifications] = useState<NotificationToast[]>([]);

  const addToast = useCallback((notification: Omit<NotificationToast, "key">) => {
    const key = Date.now().toString();
    const newNotification = { ...notification, key };
    setNotifications((notifications) => [...notifications, newNotification]);
  }, []);

  const removeToast = useCallback((key: string) => {
    setNotifications((notifications) => notifications.filter((n) => n.key !== key));
  }, []);

  useEffect(() => {
    CommonEvents.on("PUBLISH_TOAST", addToast);
    return () => {
      CommonEvents.off("PUBLISH_TOAST", addToast);
    };
  }, [addToast]);

  return (
    <>
      {notifications.map((notification, i) => {
        const { action } = notification;
        return (
          <Toast.Root
            className="flex relative bg-surface-prm rounded-md p-4 outline-none focus:outline-none BoxShadow ToastRoot"
            key={`${notification.key}`}
            onOpenChange={(open) => !open && removeToast(notification.key)}
            duration={notification.duration}
          >
            <div className="flex">
              <div
                className={twMerge(
                  "h-full mr-2 w-1 rounded-full bg-surface-cst",
                  classNames({
                    "bg-red-3": notification.titleColor === "negative",
                    "bg-green-3": notification.titleColor === "positive",
                    "bg-orange-3": notification.titleColor === "warning",
                    "bg-blue-3": notification.titleColor === "info",
                  })
                )}
              />
              <div className="flex flex-col gap-1">
                <Toast.Title
                  className={classNames("font-semibold font-sans text-md", {
                    "text-red-3": notification.titleColor === "negative",
                    "text-green-3": notification.titleColor === "positive",
                    "text-orange-3": notification.titleColor === "warning",
                    "text-blue-3": notification.titleColor === "info",
                  })}
                >
                  {notification.title}
                </Toast.Title>
                <Toast.Description className="text-glyph-scd">{notification.content}</Toast.Description>
              </div>
              <div className="flex flex-col items-end justify-center ml-auto">
                {action && (
                  <Toast.Action className="ml-1" asChild altText={action.label}>
                    <Button
                      className={classNames("p-2", {
                        "bg-red-3 text-glyph-cst-prm": action.color === "negative" && action.filled,
                        "bg-green-3 text-glyph-cst-prm": action.color === "positive" && action.filled,
                        "bg-orange-3 text-glyph-cst-prm": action.color === "warning" && action.filled,
                        "bg-blue-3 text-glyph-cst-prm": action.color === "info" && action.filled,
                        "bg-surface-cst text-glyph-cst-prm": !action.color && action.filled,
                        "text-red-3": action.color === "negative",
                        "text-green-3": action.color === "positive",
                        "text-orange-3": action.color === "warning",
                        "text-blue-3": action.color === "info",
                      })}
                      onClick={() => action?.onClick()}
                    >
                      {action.label}
                    </Button>
                  </Toast.Action>
                )}
              </div>
            </div>
            {!notification.action && (
              <Toast.Close className="absolute top-3 right-3 text-glyph-trt">
                <PiXBold />
              </Toast.Close>
            )}
          </Toast.Root>
        );
      })}
    </>
  );
};

export function withNotificationToasts<T extends JSX.IntrinsicAttributes>(Component: FC<T>) {
  // eslint-disable-next-line mobx/missing-observer
  return function WithNotificationsComponent(props: ComponentProps<typeof Component>) {
    const [notifications, setNotifications] = useState<NotificationToast[]>([]);
    return (
      <Toast.Provider swipeDirection="right">
        <NotificationsContext.Provider value={{ notifications, setNotifications }}>
          <Component {...props} />
        </NotificationsContext.Provider>
        <Toast.Viewport className="fixed bottom-0 right-0 flex flex-col gap-3 p-6 w-[390px] max-w-full m-0 list-none outline-none focus:outline-none z-notification-toast" />
      </Toast.Provider>
    );
  };
}
